/**
 * Which name the "Managing" banner shows when a coach enters manage mode through a
 * `?coachManage=<clientUid>` deep link, and where that name came from.
 *
 * The decision lives in its own module, apart from the service that performs the reads,
 * because the ORDER the sources are tried in is the part that has to stay right: getting it
 * wrong puts a raw 28-character user id in front of a coach (#4902), and nothing about that
 * failure is loud — the banner simply reads wrong. Why each source exists is in
 * `design-docs/coach-mode.md` ("Naming the managed client").
 */

/** Where the chosen name came from. Reported with the telemetry for the fallback case, so a
 *  reader can tell "the roster had no such client" apart from "the read never worked". */
export enum CoachManageNameSource {
  /** The roster in the database that is open right now — the coach's own, when no manage
   *  session is active yet. This is the only source the deep link had before #4902. */
  ActiveDbRoster = 'active-db-roster',
  /** The roster read out of the coach's own database while a DIFFERENT database (a managed
   *  client's) is the open one. */
  OwnDbRoster = 'own-db-roster',
  /** The name this device already stored for this same client when manage mode was entered
   *  the first time. */
  StoredManageState = 'stored-manage-state',
  /** Nothing named the client, so the banner falls back to their user id. */
  ClientUidFallback = 'client-uid-fallback',
}

export interface CoachManageDisplayNameInputs {
  /** The client the deep link names. */
  clientUid: string;
  /** Nickname found in the roster read from the currently-open database, if any. */
  activeDbNickname?: string;
  /** Nickname found in the roster read from the coach's OWN database, if any. */
  ownDbNickname?: string;
  /** The manage state already on this device, if a manage session is active. */
  storedManagingClient?: { clientUid: string; clientDisplayName: string } | null;
}

export interface CoachManageDisplayNameDecision {
  displayName: string;
  source: CoachManageNameSource;
}

/** Blank, or whitespace only, counts as "no name" — a nickname of spaces would otherwise
 *  win and render an empty banner, which is harder to diagnose than the uid. */
function usableName(candidate: string | undefined): string | null {
  const trimmed = (candidate ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Pick the name to show, trying the sources worst-case-last.
 *
 * The stored-state source deliberately refuses a stored name that IS the client's uid: a
 * session entered before #4902 was fixed stored exactly that, and carrying it forward would
 * keep the wrong banner alive AND hide the fallback from telemetry.
 */
export function pickCoachManageDisplayName(
  inputs: CoachManageDisplayNameInputs,
): CoachManageDisplayNameDecision {
  const fromActiveDb = usableName(inputs.activeDbNickname);
  if (fromActiveDb) return { displayName: fromActiveDb, source: CoachManageNameSource.ActiveDbRoster };

  const fromOwnDb = usableName(inputs.ownDbNickname);
  if (fromOwnDb) return { displayName: fromOwnDb, source: CoachManageNameSource.OwnDbRoster };

  const stored = inputs.storedManagingClient;
  if (stored && stored.clientUid === inputs.clientUid) {
    const fromStored = usableName(stored.clientDisplayName);
    if (fromStored && fromStored !== inputs.clientUid) {
      return { displayName: fromStored, source: CoachManageNameSource.StoredManageState };
    }
  }

  return { displayName: inputs.clientUid, source: CoachManageNameSource.ClientUidFallback };
}

/**
 * The nickname a coach gave one client, out of a roster document, or `undefined`.
 *
 * A roster read that answers an EMPTY document is not the same as a coach with no clients —
 * during a manage session that is what a read of the wrong database looks like (#4902). This
 * helper deliberately does not try to tell those apart; deciding what an absent nickname means
 * is the caller's job, and `pickCoachManageDisplayName` is where that decision lives.
 */
export function findClientNickname(
  roster: { clients?: { clientUid: string; nickname: string }[] } | null | undefined,
  clientUid: string,
): string | undefined {
  return roster?.clients?.find(c => c.clientUid === clientUid)?.nickname;
}
